import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/shared/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { getApiBaseUrl } from "@/lib/config";
import { getApiErrorMessage } from "@/lib/api";
import { AlertTriangle, ArrowRight, CheckCircle2, Clock3, Loader2, Sparkles } from "lucide-react";

const API_BASE_URL = getApiBaseUrl();
const SUCCESS_STATUSES = new Set(["paid", "authorized", "active"]);
const FAILURE_STATUSES = new Set(["cancelled", "rejected", "failed", "expired"]);
const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 3000;

const PLAN_LABELS = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

export default function MercadoPagoSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { language } = useLanguage();
  const [viewState, setViewState] = useState({ status: "loading", message: "", planName: "", attempt: 0 });

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const sessionId = query.get("session_id");
  const preapprovalId = query.get("preapproval_id");
  const paymentId = query.get("payment_id") || query.get("collection_id");
  const planId = query.get("plan_id");
  const country = query.get("country") || "CO";
  const canceled = query.get("canceled") === "1";
  const returnedStatus = query.get("status");
  const returnedStatusDetail = query.get("status_detail");

  const fallbackPlanName = PLAN_LABELS[planId] || (planId ? `${planId.charAt(0).toUpperCase()}${planId.slice(1)}` : "Voxara");

  const copy = {
    loadingTitle: language === "es" ? "Estamos confirmando tu membresía" : "We are confirming your membership",
    loadingBody: language === "es"
      ? "Gracias por tu pago. Estamos validando tu compra con Mercado Pago para activar tu plan en Voxara."
      : "Thanks for your payment. We are validating your Mercado Pago purchase to activate your Voxara plan.",
    successTitle: language === "es" ? "¡Gracias por tu compra!" : "Thanks for your purchase!",
    successBody: language === "es"
      ? "Tu membresía ya quedó activa. Ya puedes volver al dashboard y usar tu nuevo plan."
      : "Your membership is now active. You can return to the dashboard and use your new plan.",
    failureTitle: language === "es" ? "No pudimos confirmar el pago todavía" : "We could not confirm the payment yet",
    canceledBody: language === "es"
      ? "El pago no se completó. Si quieres, puedes intentar de nuevo desde facturación."
      : "The payment was not completed. You can retry from billing.",
    pendingTitle: language === "es" ? "Tu pago sigue en validación" : "Your payment is still being validated",
    pendingBody: language === "es"
      ? "Estamos verificando tu pago con Mercado Pago. En unos segundos confirmaremos tu membresía automáticamente."
      : "We are verifying your Mercado Pago payment. In a few seconds we will confirm your membership automatically.",
    dashboard: language === "es" ? "Ir al dashboard" : "Go to dashboard",
    billing: language === "es" ? "Volver a facturación" : "Back to billing",
    badge: language === "es" ? "Membresía adquirida" : "Membership acquired",
    progress: language === "es" ? "Intento" : "Attempt",
  };

  const resolveSuccessState = useCallback(async () => {
    if (!sessionId && !preapprovalId) {
      setViewState({ status: "error", message: language === "es" ? "No encontramos la sesión de pago para mostrar esta confirmación." : "We could not find the payment session for this confirmation.", planName: fallbackPlanName, attempt: 0 });
      return;
    }

    if (canceled && !preapprovalId) {
      setViewState({ status: "error", message: copy.canceledBody, planName: fallbackPlanName, attempt: 0 });
      return;
    }

    for (let attempt = 1; attempt <= MAX_POLL_ATTEMPTS; attempt += 1) {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));
        continue;
      }

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));
        continue;
      }

      try {
        const checkoutRequest = sessionId
          ? axios.get(`${API_BASE_URL}/subscription/checkout-status/${sessionId}`, {
              params: paymentId ? { payment_id: paymentId } : undefined,
            })
          : axios.get(`${API_BASE_URL}/subscription/mercadopago/direct-status`, {
              params: {
                preapproval_id: preapprovalId,
                payment_id: paymentId || undefined,
                plan_id: planId,
                country,
              },
            });

        const [checkoutResponse, subscriptionResponse] = await Promise.all([
          checkoutRequest,
          axios.get(`${API_BASE_URL}/subscription/status`),
        ]);

        const checkout = checkoutResponse.data || {};
        const subscription = subscriptionResponse.data || {};
        const resolvedPlanName = checkout.plan_name || PLAN_LABELS[subscription.plan_id] || fallbackPlanName;
        const checkoutStatus = String(checkout.payment_status || checkout.status || returnedStatus || "").toLowerCase();
        const subscriptionStatus = String(subscription.status || "").toLowerCase();
        const hasPlanAccess = subscription.plan_id && subscription.plan_id !== "free";
        const paymentStatusDetail = checkout.provider_payment_status_detail || returnedStatusDetail;

        if (SUCCESS_STATUSES.has(checkoutStatus) || (hasPlanAccess && !FAILURE_STATUSES.has(subscriptionStatus))) {
          await refreshUser();
          setViewState({ status: "success", message: copy.successBody, planName: resolvedPlanName, attempt });
          return;
        }

        if (FAILURE_STATUSES.has(checkoutStatus) || FAILURE_STATUSES.has(subscriptionStatus)) {
          const failureMessage = paymentStatusDetail
            ? `${copy.canceledBody} ${language === "es" ? `Mercado Pago reportó: ${paymentStatusDetail}.` : `Mercado Pago reported: ${paymentStatusDetail}.`}`
            : copy.canceledBody;
          setViewState({ status: "error", message: getApiErrorMessage({ response: { data: { detail: failureMessage } } }, failureMessage, language), planName: resolvedPlanName, attempt });
          return;
        }

        setViewState({ status: "processing", message: copy.pendingBody, planName: resolvedPlanName, attempt });
      } catch (error) {
        if (attempt === MAX_POLL_ATTEMPTS) {
          setViewState({
            status: "error",
            message: getApiErrorMessage(error, language === "es" ? "No pudimos confirmar tu membresía todavía." : "We could not confirm your membership yet.", language),
            planName: fallbackPlanName,
            attempt,
          });
          return;
        }
      }

      await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));
    }

    setViewState({ status: "processing", message: copy.pendingBody, planName: fallbackPlanName, attempt: MAX_POLL_ATTEMPTS });
  }, [canceled, copy.canceledBody, copy.pendingBody, copy.successBody, country, fallbackPlanName, language, paymentId, planId, preapprovalId, refreshUser, returnedStatus, returnedStatusDetail, sessionId]);

  useEffect(() => {
    resolveSuccessState();
  }, [resolveSuccessState]);

  const isSuccess = viewState.status === "success";
  const isError = viewState.status === "error";

  return (
    <div className="min-h-screen bg-background" data-testid="mercadopago-success-page">
      <Header />
      <main className="mx-auto flex min-h-screen max-w-4xl items-center px-4 pb-10 pt-28 sm:px-6">
        <Card className="w-full overflow-hidden border-border/70 bg-card/95 shadow-2xl" data-testid="mercadopago-success-card">
          <div className="bg-[radial-gradient(circle_at_top,rgba(248,0,248,0.24),transparent_58%)] p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="speaker-edit-cta border-0 px-4 py-2 text-sm font-semibold" data-testid="mercadopago-success-badge">
                <Sparkles className="mr-2 h-4 w-4" />
                {copy.badge}
              </Badge>
              <Badge variant="outline" data-testid="mercadopago-success-plan-badge">{viewState.planName || fallbackPlanName}</Badge>
            </div>

            <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="max-w-2xl">
                <h1 className="text-4xl font-bold text-foreground" data-testid="mercadopago-success-title">
                  {isSuccess ? copy.successTitle : isError ? copy.failureTitle : copy.loadingTitle}
                </h1>
                <p className="mt-4 text-base leading-7 text-muted-foreground" data-testid="mercadopago-success-message">
                  {viewState.message || copy.loadingBody}
                </p>
                {isSuccess ? (
                  <p className="mt-4 text-lg font-semibold text-emerald-300" data-testid="mercadopago-success-membership-copy">
                    {language === "es"
                      ? `Gracias, adquiriste la membresía ${viewState.planName || fallbackPlanName}.`
                      : `Thanks, you acquired the ${viewState.planName || fallbackPlanName} membership.`}
                  </p>
                ) : null}
              </div>

              <div
                className={`rounded-3xl border p-5 md:min-w-[220px] ${
                  isSuccess
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : isError
                    ? "border-red-500/40 bg-red-500/10 text-red-300"
                    : "border-border/70 bg-muted/40 text-foreground"
                }`}
                data-testid="mercadopago-success-status-panel"
              >
                <div className="flex items-center gap-3">
                  {isSuccess ? <CheckCircle2 className="h-7 w-7" /> : isError ? <AlertTriangle className="h-7 w-7" /> : <Loader2 className="h-7 w-7 animate-spin" />}
                  <div>
                    <p className="text-sm uppercase tracking-[0.22em] opacity-80">Mercado Pago</p>
                    <p className="mt-1 text-lg font-semibold">{isSuccess ? "Confirmado" : isError ? "Pendiente" : "Validando"}</p>
                  </div>
                </div>
                {!isSuccess ? (
                  <p className="mt-4 text-sm opacity-80" data-testid="mercadopago-success-attempt-copy">
                    {copy.progress} {viewState.attempt || 1}/{MAX_POLL_ATTEMPTS}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="border-t border-border/70 p-6 sm:p-8">
            <div className="rounded-3xl border border-border/70 bg-muted/20 p-5" data-testid="mercadopago-success-next-step-panel">
              <div className="flex items-start gap-3">
                <Clock3 className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Voxara</p>
                  <p className="mt-2 text-base leading-7 text-foreground">
                    {isSuccess
                      ? (language === "es" ? "Tu cuenta ya fue actualizada. Desde aquí puedes volver directamente al dashboard." : "Your account has already been updated. From here you can go straight back to the dashboard.")
                      : (language === "es"
                        ? "Quédate en esta página mientras validamos la compra. Si el pago ya fue aprobado, la membresía se activará automáticamente."
                        : "Stay on this page while we validate your purchase. If the payment was already approved, the membership will activate automatically.")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="mercadopago-success-actions">
              <Button onClick={() => navigate("/dashboard")} className="speaker-edit-cta min-h-12 px-6 text-base font-semibold" data-testid="mercadopago-success-dashboard-button">
                {copy.dashboard}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {isError ? (
                <Button variant="outline" onClick={() => navigate("/billing")} data-testid="mercadopago-success-billing-button">
                  {copy.billing}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}